// src/resources/Drivers/DriverAssignmentsSection.js
import React, { useEffect, useState } from "react";
import { useRecordContext } from "react-admin";
import { db } from "../../firebase/firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { assignDriverToVehicle } from "../../utils/assignDriverToVehicle";

// 🟦 MUI dialog imports
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@mui/material";

export const DriverAssignmentsSection = () => {
  const record = useRecordContext(); // current driver in Edit
  const [vehicles, setVehicles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [driverRole, setDriverRole] = useState("main");
  const [monthlyInstallment, setMonthlyInstallment] = useState("");
  const [secondDriverExpense, setSecondDriverExpense] = useState("500");
// ADD BELOW newStartDate state
const [showUnassignDialog, setShowUnassignDialog] = useState(false);
const [unassignEndDate, setUnassignEndDate] = useState("");

  // For reassignment dialog
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [oldEndDate, setOldEndDate] = useState("");
  const [newStartDate, setNewStartDate] = useState("");

  // ✅ Make driverId / driverName safe even if record is null
  const driverId =
  record?.id ||
  record?.driverId ||     // fallback if stored manually
  record?._id ||           // fallback if provider uses `_id`
  null;

  const driverName = record?.fullName || "";

  // 🔹 Load all vehicles (for dropdown)
  useEffect(() => {
    const q = collection(db, "vehicles");
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setVehicles(list);
    });

    return () => unsub();
  }, []);

  // 🔹 Load this driver's assignment history
  useEffect(() => {
    if (!driverId) return; // safe guard *inside* hook

    const q = query(
      collection(db, "driverCarAssignments"),
      where("driverId", "==", driverId),
      orderBy("startDate", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setAssignments(list);

      // Find active assignment (no endDate)
      const active = list.find((a) => !a.endDate);
      setActiveAssignment(active || null);
    });

    return () => unsub();
  }, [driverId]);

  // ✅ Now it's safe to early-return; hooks already executed
  if (!record) {
    return null;
  }

  // 🔹 Display assignment dates as UAE calendar dates.
  // Firestore timestamps are absolute instants; using toISOString() converts them
  // to UTC and can make a UAE midnight date appear as the previous day.
  // This formatter is display-only and does not modify stored assignment dates.
  const formatDate = (value) => {
    if (!value) return "-";

    if (value.toDate) {
      const d = value.toDate();
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Dubai",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(d);

      const year = parts.find((part) => part.type === "year")?.value;
      const month = parts.find((part) => part.type === "month")?.value;
      const day = parts.find((part) => part.type === "day")?.value;

      return year && month && day ? `${year}-${month}-${day}` : String(value);
    }

    return String(value);
  };

  // 🟢 Main "Save Assignment" button click
  const handleAddAssignment = async () => {
    if (!vehicleId) {
      alert("Please select a vehicle.");
      return;
    }

    const vehicle = vehicles.find((v) => v.id === vehicleId);
    if (!vehicle) {
      alert("Vehicle not found.");
      return;
    }

    // 👉 If there is an active assignment and vehicle is different → REASSIGN
    if (activeAssignment && activeAssignment.vehicleId !== vehicleId) {
      // open dialog asking for oldEndDate + newStartDate
      setOldEndDate("");
      setNewStartDate("");
      setShowReassignDialog(true);
      return;
    }

    // 👉 If already assigned to same car
    if (activeAssignment && activeAssignment.vehicleId === vehicleId) {
      alert("This driver is already assigned to this vehicle.");
      return;
    }

    // 👉 No active assignment → normal first-time assignment (use Start Date from main form)
    if (!startDate) {
      alert("Please select a start date.");
      return;
    }

    try {
      await assignDriverToVehicle({
  driverId,
  driverName,
  vehicleId,
  vehicleNumber: vehicle.vehicleNumber || "",
  vehicleType: vehicle.vehicleType || "",
  company: vehicle.company || "",
  startDate, // ← CORRECT PARAMETER
  driverRole,
  monthlyInstallment,
  secondDriverMonthlyExpense: secondDriverExpense,
  costConfigured:
    driverRole === "second" || Number(monthlyInstallment || 0) > 0,
  installmentEffectiveFrom: startDate,
});


      setVehicleId("");
      setStartDate("");
      setDriverRole("main");
      setMonthlyInstallment("");
      setSecondDriverExpense("500");
      alert("✅ Assignment saved.");
    } catch (err) {
      console.error("Error saving assignment:", err);
      alert("❌ Failed: " + err.message);
    }
  };

  // 🟣 Confirm in REASSIGN dialog
  const handleConfirmReassign = async () => {
    if (!oldEndDate) {
      alert("Please select OLD vehicle end date.");
      return;
    }
    if (!newStartDate) {
      alert("Please select NEW vehicle start date.");
      return;
    }
    if (newStartDate <= oldEndDate) {
      alert("New car start date must be AFTER old car end date.");
      return;
    }

    const vehicle = vehicles.find((v) => v.id === vehicleId);
    if (!vehicle) {
      alert("Vehicle not found.");
      return;
    }

    try {
      await assignDriverToVehicle({
        mode: "reassign",
        driverId,
        driverName,
        vehicleId,
        vehicleNumber: vehicle.vehicleNumber || "",
        vehicleType: vehicle.vehicleType || "",
        company: vehicle.company || "",
        oldEndDate,
        newStartDate,
        driverRole,
        monthlyInstallment,
        secondDriverMonthlyExpense: secondDriverExpense,
        costConfigured:
          driverRole === "second" || Number(monthlyInstallment || 0) > 0,
        installmentEffectiveFrom: newStartDate,
      });

      setShowReassignDialog(false);
      setVehicleId("");
      setStartDate("");
      setDriverRole("main");
      setMonthlyInstallment("");
      setSecondDriverExpense("500");
      alert("✅ Reassignment saved.");
    } catch (err) {
      console.error("Error reassigning:", err);
      alert("❌ Failed: " + err.message);
    }
  };
  const handleConfirmUnassign = async () => {
  if (!unassignEndDate) {
    alert("Please select end date.");
    return;
  }
  if (!activeAssignment) return;

  try {
    await assignDriverToVehicle({
      mode: "unassign",
      driverId,
      vehicleId: activeAssignment.vehicleId,
      unassignEndDate,
    });

    alert("Driver unassigned successfully.");
    setShowUnassignDialog(false);
    setUnassignEndDate("");
  } catch (err) {
    console.error("Error unassigning:", err);
    alert("❌ Failed: " + err.message);
  }
};


  return (
    <>
      <div
        style={{
          marginTop: 24,
          padding: 16,
          border: "1px solid #ddd",
          borderRadius: 8,
          background: "#fafafa",
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Driver – Car Assignments</h3>
        <p style={{ fontSize: 12, color: "#555" }}>
          Use this section to set which car this driver is working on, with dates.
          Earnings from Uber / Yango / manual trips / POS can later use this
          assignment history by date.
        </p>

        {/* 🔹 Add / update assignment */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Vehicle
            </label>
            <select
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              style={{ padding: 6, minWidth: 220 }}
            >
              <option value="">Select vehicle</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.vehicleNumber || "No Number"} – {v.vehicleType || ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Start Date (for first assignment)
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ padding: 6 }}
            />
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Driver Role
            </label>
            <select
              value={driverRole}
              onChange={(e) => setDriverRole(e.target.value)}
              style={{ padding: 6, minWidth: 150 }}
            >
              <option value="main">Main Driver</option>
              <option value="second">Second Driver</option>
            </select>
          </div>

          {driverRole === "main" ? (
            <div>
              <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
                Monthly Installment / Fixed Cost
              </label>
              <input
                type="number"
                value={monthlyInstallment}
                onChange={(e) => setMonthlyInstallment(e.target.value)}
                placeholder="Leave blank to keep old logic"
                style={{ padding: 6, minWidth: 190 }}
              />
            </div>
          ) : (
            <div>
              <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
                Second Driver Monthly Expense
              </label>
              <input
                type="number"
                value={secondDriverExpense}
                onChange={(e) => setSecondDriverExpense(e.target.value)}
                style={{ padding: 6, minWidth: 150 }}
              />
            </div>
          )}

          <button
            type="button"
            onClick={handleAddAssignment}
            style={{
              padding: "8px 14px",
              borderRadius: 6,
              border: "none",
              background: "#1976d2",
              color: "white",
              fontWeight: 600,
              cursor: "pointer",
              marginTop: 20,
            }}
          >
            💾 Save Assignment
          </button>
        </div>

        {/* 🔹 Assignment history table */}
        <div style={{ marginTop: 10 }}>
          <h4 style={{ marginBottom: 8 }}>Assignment History</h4>
          {assignments.length === 0 ? (
            <p style={{ fontSize: 12, color: "#777" }}>
              No assignments yet for this driver.
            </p>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      borderBottom: "1px solid #ccc",
                      textAlign: "left",
                      padding: 6,
                    }}
                  >
                    Vehicle
                  </th>
                  <th
                    style={{
                      borderBottom: "1px solid #ccc",
                      textAlign: "left",
                      padding: 6,
                    }}
                  >
                    Type
                  </th>
                  <th
                    style={{
                      borderBottom: "1px solid #ccc",
                      textAlign: "left",
                      padding: 6,
                    }}
                  >
                    Start Date
                  </th>
                  {/* ✅ FIX — Add missing End Date column */}
    <th
      style={{
        borderBottom: "1px solid #ccc",
        textAlign: "left",
        padding: 6,
      }}
    >
      End Date
    </th>
                  <th style={{ borderBottom: "1px solid #ccc", textAlign: "left", padding: 6 }}>
                    Role
                  </th>
                  <th style={{ borderBottom: "1px solid #ccc", textAlign: "left", padding: 6 }}>
                    Cost Setup
                  </th>
                  <th
  style={{
    borderBottom: "1px solid #ccc",
    textAlign: "left",
    padding: 6,
  }}
>
  Action
</th>

                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id}>
                    <td
                      style={{
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      {a.vehicleNumber || "-"}
                    </td>
                    <td
                      style={{
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      {a.vehicleType || "-"}
                    </td>
                    <td
                      style={{
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      {formatDate(a.startDate)}
                    </td>
                    <td
                      style={{
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      {a.endDate ? formatDate(a.endDate) : "Present"}
                    </td>
                    <td style={{ borderBottom: "1px solid #eee", padding: 6 }}>
                      {a.driverRole === "second" ? "Second Driver" : a.driverRole === "main" ? "Main Driver" : "Not set"}
                    </td>
                    <td style={{ borderBottom: "1px solid #eee", padding: 6 }}>
                      {a.costConfigured ? (
                        a.driverRole === "second"
                          ? `Extra AED ${Number(a.secondDriverMonthlyExpense || 500).toFixed(2)} / month`
                          : `Installment AED ${Number(a.monthlyInstallment || 0).toFixed(2)} / month`
                      ) : (
                        "Old logic / not configured"
                      )}
                    </td>
                    <td style={{ padding: 6 }}>
  {!a.endDate && (
    <button
    type="button"
      style={{
        padding: "4px 10px",
        fontSize: 11,
        background: "#d32f2f",
        color: "white",
        borderRadius: 4,
        border: "none",
        cursor: "pointer"
      }}
      onClick={() => {
        setActiveAssignment(a);
        setShowUnassignDialog(true);
      }}
    >
      Unassign
    </button>
  )}
</td>

                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 🟦 Reassignment Dialog */}
      <Dialog
        open={showReassignDialog}
        onClose={() => setShowReassignDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reassign Vehicle</DialogTitle>
        <DialogContent dividers>
          {activeAssignment && (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>Current Vehicle:</strong>{" "}
                {activeAssignment.vehicleNumber || "-"} (
                {activeAssignment.vehicleType || "-"})
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Assigned Since:</strong>{" "}
                {formatDate(activeAssignment.startDate)}
              </Typography>
            </>
          )}

          <TextField
            label="Old Vehicle End Date"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={oldEndDate}
            onChange={(e) => setOldEndDate(e.target.value)}
          />

          <TextField
            label="New Vehicle Start Date"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={newStartDate}
            onChange={(e) => setNewStartDate(e.target.value)}
          />

          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            New assignment cost setup
          </Typography>

          <select
            value={driverRole}
            onChange={(e) => setDriverRole(e.target.value)}
            style={{ padding: 8, width: "100%", marginBottom: 12 }}
          >
            <option value="main">Main Driver</option>
            <option value="second">Second Driver</option>
          </select>

          {driverRole === "main" ? (
            <TextField
              label="Monthly Installment / Fixed Cost"
              type="number"
              fullWidth
              margin="normal"
              value={monthlyInstallment}
              onChange={(e) => setMonthlyInstallment(e.target.value)}
              helperText="Leave blank to keep old copied/fetched installment logic."
            />
          ) : (
            <TextField
              label="Second Driver Monthly Expense"
              type="number"
              fullWidth
              margin="normal"
              value={secondDriverExpense}
              onChange={(e) => setSecondDriverExpense(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setShowReassignDialog(false)}>Cancel</Button>

<Button
  type="button"
  onClick={handleConfirmReassign}
  variant="contained"
  color="primary"
>
  Save Reassignment
</Button>

        </DialogActions>
      </Dialog>
      {/* 🟥 Unassign Dialog */}
<Dialog
  open={showUnassignDialog}
  onClose={() => setShowUnassignDialog(false)}
  maxWidth="xs"
  fullWidth
>
  <DialogTitle>Unassign Vehicle</DialogTitle>

  <DialogContent dividers>
    {activeAssignment && (
      <>
        <Typography variant="body2" gutterBottom>
          <strong>Vehicle:</strong> {activeAssignment.vehicleNumber}
        </Typography>

        <Typography variant="body2" gutterBottom>
          <strong>Assigned Since:</strong>{" "}
          {formatDate(activeAssignment.startDate)}
        </Typography>
      </>
    )}

    <TextField
      label="End Date"
      type="date"
      fullWidth
      margin="normal"
      InputLabelProps={{ shrink: true }}
      value={unassignEndDate}
      onChange={(e) => setUnassignEndDate(e.target.value)}
    />
  </DialogContent>

  <DialogActions>
    <Button type="button" onClick={() => setShowUnassignDialog(false)}>Cancel</Button>

<Button
  type="button"
  variant="contained"
  color="error"
  onClick={handleConfirmUnassign}
>
  Unassign
</Button>

  </DialogActions>
</Dialog>

    </>
    
  );
};
